/* eslint-disable */
/**
 * utils/index.js — Re-exports all utilities for backward compatibility.
 * Import from "./utils" continues to work unchanged.
 */
import { useState, useEffect } from "react";
import { supabase } from "../supabase";
import { getBreakpoint } from "./cssVars";

// ── Re-export all split modules ──────────────────────────────────────────────
export * from './imageUtils';
export * from './exportUtils';
export * from './searchUtils';
export * from './skuUtils';
export * from './devStageUtils';

// ── Shared utilities (don't belong to a specific module) ─────────────────────
// Optional `options.signal` (AbortSignal) lets callers cancel an in-flight
// retry chain when a new query supersedes this one. The signal does NOT
// cancel the underlying network fetch unless the queryFn passes it through
// to Supabase via `.abortSignal(signal)` — pass it both places when you
// want full cancellation. See useItemsQuery for the canonical pattern.
function _isAbortError(err) {
  return err && (err.name === 'AbortError' || err.code === 20);
}
export async function supabaseWithRetry(queryFn, maxRetries = 2, options = {}) {
  const { signal } = options;
  for (let i = 0; i <= maxRetries; i++) {
    // skip retry on abort — request was superseded
    if (signal?.aborted) throw signal.reason || new DOMException('Aborted', 'AbortError');
    let result;
    try {
      result = await queryFn();
    } catch (err) {
      // skip retry on abort — request was superseded
      if (_isAbortError(err) || signal?.aborted) throw err;
      if (i === maxRetries) throw err;
      await new Promise(r => setTimeout(r, 1000 * (i + 1)));
      continue;
    }
    if (!result.error) return result;
    // Supabase surfaces network aborts as a result.error with name 'AbortError'.
    if (_isAbortError(result.error) || signal?.aborted) {
      throw result.error || (signal?.reason || new DOMException('Aborted', 'AbortError'));
    }
    if (i === maxRetries) return result;
    await new Promise(r => setTimeout(r, 1000 * (i + 1)));
  }
}

export function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < getBreakpoint('mobile'));
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < getBreakpoint('mobile'));
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return isMobile;
}

export function useIsTablet() {
  const [isTablet, setIsTablet] = useState(() => window.innerWidth >= getBreakpoint('mobile') && window.innerWidth < getBreakpoint('tablet'));
  useEffect(() => {
    const fn = () => setIsTablet(window.innerWidth >= getBreakpoint('mobile') && window.innerWidth < getBreakpoint('tablet'));
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return isTablet;
}

export function useResponsiveCols(userSetting) {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  if (isMobile) return 2;
  if (isTablet) return 4;
  return userSetting || 6;
}
